import { FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import CategoryCard from '../../molecules/CategoryCard';
import CategoryPreviewSubCategoryList from '../../molecules/CategoryPreviewSubCategoryList';

import { RootState } from 'store/types';

import ProCTALink from 'components/atoms/ProCTALink';
import styles from './CategoryLandingPageList.module.scss';

export interface Category {
  categoryLevel?: 'c1' | 'c2' | 'c3' | 'vert';
  children?: Record<string, Category>;
  id: string;
  imageUrl?: string;
  pageBanner?: string;
  pageRequestFormTitle?: string;
  parentCategories?: Array<string>;
  parentId?: string;
  thumbUrl?: string;
  title: string;
}

interface Vertical {
  _id: string;
  categories: Array<string>;
  imageUrl?: string;
  thumbUrl?: string;
  translations: {
    newRequestFormTitle: string;
    requestType: string;
  };
}

interface PopularVertical {
  id: string;
  imageUrl?: string;
  thumbUrl?: string;
  translations: {
    requestType: string;
  };
}

interface Props {
  categoryTree: Category;
  popular: Array<PopularVertical>;
  verticals: Array<Vertical>;
}

const CategoryLandingPageList: FC<Props> = ({ categoryTree, popular, verticals }) => {
  const { categoryLevel, children, title, id, parentId } = categoryTree;
  const { t } = useTranslation('marketingPages');
  const relatedCategories = useSelector(
    // @ts-expect-error fix jobCategories type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (state: RootState) => state.jobCategories.tree.children[parentId] as Category
  );

  let relatedC2Categories: Array<Category> = [];
  if (categoryLevel === 'c3') {
    relatedC2Categories = Object.values(relatedCategories.children ?? {}).filter(
      (category) => category.id !== id
    );
  }

  const mapVerticalToCategory = (category: Category) => {
    const mapped = verticals
      .filter((vertical) => vertical.categories.includes(category.id))
      .map((vertical) => ({
        id: vertical._id,
        imageUrl: vertical.imageUrl,
        title: vertical.translations.requestType,
        parentCategories: vertical.categories,
      }));
    return mapped;
  };

  const subCategories: Array<Category> =
    children && Object.values(children).length > 0
      ? Object.values(children)
      : mapVerticalToCategory(categoryTree);

  const c3Verticals = Object.values(verticals)
    .filter((vert) => vert.categories.includes(id))
    .map((vert) => ({
      id: vert._id,
      imageUrl: vert.thumbUrl,
      title: vert.translations.requestType,
    }));

  // eslint-disable-next-line no-magic-numbers
  const popularVerts = popular.slice(0, 6);

  const getPopularClickUrl = (popularId: string) => {
    const popularVertical = verticals.find((vertical) => vertical._id === popularId);
    if (!popularVertical) return;
    const { categories, _id } = popularVertical;
    // Path has category 2 as ternary because events category doesn't have this.
    const newPath = `/${categories[0]}${categories[1] ? `/${categories[1]}` : ''}${
      categories[2] ? `/${categories[2]}` : ''
    }${_id ? `/${_id}` : ''}`;
    return newPath;
  };

  const getSubCategories = (category: Category) => {
    if (id === 'events') {
      return mapVerticalToCategory(category);
    }
    return Object.values(category.children ?? []);
  };

  return (
    <>
      <section className={styles['section']} data-cy={'popular-service-category-page'}>
        <h3 className={styles['title']}>{title}関連の人気のサービス</h3>
        <div className={styles['category-card-group']}>
          {popularVerts.map((category) => (
            <CategoryCard
              link={getPopularClickUrl(category.id)}
              key={category.id}
              description={category.translations.requestType}
              imgAlt={category.translations.requestType}
              imgUrl={category.thumbUrl ?? ''}
            />
          ))}
        </div>
      </section>
      <div className={styles['divider']} />
      <section className={styles['section']}>
        {categoryLevel !== 'c3' && <h3 className={styles['title']}>{title}関連のカテゴリー</h3>}
        {categoryLevel !== 'c3' &&
          subCategories.map((category) => (
            <CategoryPreviewSubCategoryList
              verticals={verticals}
              key={category.title}
              category={category}
              topCategoryId={id}
              subCategories={getSubCategories(category)}
            />
          ))}

        {categoryLevel === 'c3' && (
          <CategoryPreviewSubCategoryList
            verticals={verticals}
            key={''}
            category={{ categoryLevel: 'vert', id, title }}
            topCategoryId={id}
            subCategories={c3Verticals}
          />
        )}
      </section>
      <section>
        <ProCTALink eventType={'verticalProCTAClicked'} eventLabel={categoryLevel} />
      </section>
      {categoryLevel === 'c3' && relatedC2Categories.length > 0 && (
        <section>
          <h3 className={styles['title']}>{t('relatedCategories')}</h3>
          <div className={styles['category-card-group']}>
            {relatedC2Categories.map(({ id, imageUrl, parentCategories, title }) => (
              <CategoryCard
                link={
                  parentCategories && parentCategories.length > 0
                    ? `/${parentCategories.join('/')}/${id}`
                    : `/${id}`
                }
                key={id}
                description={title}
                imgAlt={title}
                imgUrl={imageUrl ?? ''}
              />
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default CategoryLandingPageList;
export type { Props as CategoryLandingPageListProps };
